import { createClient } from '@supabase/supabase-js';
import { SupabaseClient } from '@supabase/supabase-js';


// Get environment variables with fallbacks for development and testing
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://djaohifjetmpwlszwirr.supabase.co';

// IMPORTANT: This is the public anon key for API authentication
// Latest key for the Supabase project - updated March 2025
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRqYW9oaWZqZXRtcHdsc3p3aXJyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDAyNjEzMDgsImV4cCI6MjA1NTgzNzMwOH0.KeY2V3EWN-J_QzS8lRIn2sRV3b2rkukrhuQy5BvhDGU';


// Ensure we have valid keys to prevent API errors
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables! Authentication will not work properly.');
}

// This function handles token persistence across page loads
const persistTokens = () => {
  try {
    const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
    const storedToken = localStorage.getItem(storageKey);
    
    if (storedToken) {
      
      // Ensure token is also available in other storage locations for compatibility
      const alternateKeys = ['supabase-auth-token', 'supabase-auth-token-v2', 'supabase.auth.token'];
      alternateKeys.forEach(key => {
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, storedToken);
        }
      });
    }
  } catch (e) {
    // Error syncing tokens ignored
  }
};

// Call persistence function
persistTokens();

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables, app may not function correctly');
}

// Create a fallback client that won't break the app but will log errors when used
const fallbackClient = {
  auth: {
    signInWithOAuth: async () => ({ error: new Error('Supabase not initialized') }),
    signOut: async () => ({ error: null }),
    getSession: async () => ({ data: { session: null } }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
  }
};

// Initialize supabase client safely
let supabase: any;

try {
  // Get site URL for session management
  const siteUrl = window.location.origin;
  
  // Clear client-side caches that might be causing issues
  if ('caches' in window) {
    caches.keys().then(cacheNames => {
      cacheNames.forEach(cacheName => {
        if (cacheName.includes('supabase') || cacheName.includes('auth')) {
          caches.delete(cacheName);
        }
      });
    });
  }
  
  // Check for existing tokens in all possible locations
  const checkExistingTokens = () => {
    const keys = [
      'supabase.auth.token',
      'sb-djaohifjetmpwlszwirr-auth-token',
      'supabase-auth-token',
      'supabase-auth-token-v2'
    ];
    keys.forEach(key => {
      try {
        const token = localStorage.getItem(key);
        if (token) {
          // Token found in storage
        }
      } catch (e) {
        // Error checking storage ignored
      }
    });
  };
  
  checkExistingTokens();
  
  // Create supabase client with improved configuration
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // Match the storage key format used by Supabase
      storageKey: `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`,
      // Add debug logs to diagnose issues in development
      debug: true,
      // Use pkce for better security than implicit flow
      flowType: 'pkce',
      // Add cookies for more resilient auth
      storage: {
        getItem: (key) => {
          try {
            // First try localStorage
            const localValue = localStorage.getItem(key);
            if (localValue) {
              return localValue;
            }
            
            // Then try cookies as fallback
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
              const [cookieName, cookieValue] = cookie.trim().split('=');
              if (cookieName === key) {
                return decodeURIComponent(cookieValue);
              }
            }
            return null;
          } catch (error) {
            console.error('Error reading from storage', error);
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            // Store in multiple localStorage keys for better compatibility
            localStorage.setItem(key, value);
            localStorage.setItem('supabase-auth-token', value);
            localStorage.setItem('supabase-auth-token-v2', value);
            localStorage.setItem('supabase.auth.token', value);
            
            // Set a cookie with longer expiration (7 days)
            const maxAge = 3600 * 24 * 7;
            document.cookie = `${key}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`;
            // Auth token stored successfully
          } catch (error) {
            console.error('Error writing to storage', error);
          }
        },
        removeItem: (key) => {
          try {
            // Clear all possible storage locations
            const keys = [
              key,
              'supabase-auth-token',
              'supabase-auth-token-v2',
              'supabase.auth.token'
            ];
            
            keys.forEach(k => {
              localStorage.removeItem(k);
              document.cookie = `${k}=;path=/;max-age=0;SameSite=Lax`;
            });
            
            // Auth tokens cleared from storage
          } catch (error) {
            console.error('Error removing from storage', error);
          }
        }
      }
    }
  });

} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  supabase = fallbackClient as any;
}

export { supabase };
